import { useCallback, useEffect, useRef, useState } from "react";

interface UseReportDataOptions<P> {
  /** Fetch fonksiyonu — signal ile iptal desteği. */
  fetcher: (params: P, signal: AbortSignal) => Promise<unknown>;
  /** Filtre parametreleri. Değişince otomatik re-fetch. */
  params: P;
  /** Debounce ms — default 300. */
  debounceMs?: number;
  /** Auto fetch kapalıysa manuel refetch ile tetiklenir. */
  enabled?: boolean;
}

interface UseReportDataResult<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * useReportData — rapor detay sayfaları için ortak data-fetch hook'u.
 * - AbortController ile önceki in-flight istek iptal edilir
 * - Filter değişiminde debounce'la re-fetch
 * - Loading/error/empty state standardizasyonu
 */
export function useReportData<T, P>(
  options: UseReportDataOptions<P>,
): UseReportDataResult<T> {
  const { fetcher, params, debounceMs = 300, enabled = true } = options;
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const execute = useCallback(
    (p: P) => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setIsLoading(true);
      setError(null);
      fetcherRef
        .current(p, ctrl.signal)
        .then((result) => {
          if (ctrl.signal.aborted) return;
          setData(result as T);
          setIsLoading(false);
        })
        .catch((err: unknown) => {
          if (ctrl.signal.aborted) return;
          // Axios CancelError veya AbortError'ı sessizce geç
          const name = (err as { name?: string })?.name;
          if (name === "AbortError" || name === "CanceledError") return;
          setError(err as Error);
          setIsLoading(false);
        });
    },
    [],
  );

  useEffect(() => {
    if (!enabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      execute(params);
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // params objesi değişiyor; referans eşitliği yerine key'ler üzerinden bağımlılık
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), enabled, debounceMs, execute]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const refetch = useCallback(() => execute(params), [execute, params]);

  return { data, isLoading, error, refetch };
}
